import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { type ChangeEvent, forwardRef, useCallback, useRef, useState } from "react";
import { SavingIndicator } from "~/common/components/CommentInput/components/SavingIndicator";

import classes from "./CommentInput.module.less";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";

interface CommentProps {
  className?: string;
  value: string;
  onChange: (comment: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const useShowAndScrollInput = (toggleVisibility: () => void) => {
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const toggleCb = () => {
    toggleVisibility();
    window.setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({ block: "center" });
      }
    });
  };
  return [inputRef, toggleCb] as const;
};

export const CommentInput = forwardRef<HTMLTextAreaElement | HTMLInputElement, CommentProps>(
  ({ value: externalValue, onChange: externalOnChange, className, label, placeholder, disabled }, ref) => {
    const [saving, setSaving] = useState(false);
    const externalOnChangeCb = useCallback(
      async (comment: string) => {
        await externalOnChange(comment);
        setSaving(false);
      },
      [externalOnChange],
    );

    const [value, setValue] = useDebouncedValue(externalValue, externalOnChangeCb, 500);
    const { t } = useTranslation();

    const helperLabel = (
      <div className={classes.commentHelper}>
        {t("wishlistPage.comment.tag")}
        <SavingIndicator saving={saving} />
      </div>
    );

    const onChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSaving(true);
        setValue(e.target.value);
      },
      [setValue],
    );

    return (
      <div className={className}>
        <TextField
          variant={"outlined"}
          label={label}
          minRows={3}
          maxRows={10}
          inputProps={{ maxLength: 140 }}
          value={value}
          onChange={onChange}
          helperText={helperLabel}
          placeholder={placeholder}
          inputRef={ref}
          disabled={disabled}
          multiline
          fullWidth
        />
      </div>
    );
  },
);
